import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import React, { useCallback, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import api from 'api'
import { useCancelScheduledReactivationMutation } from 'api/accounts/reactivateAccountWithCapture'
import { CACHE_TAG_CONFIGURED_SERVICES_LIST, CACHE_TAG_WORK_ORDER_LIST } from 'api/cacheTagTypes'
import { HHAlert } from 'components/common/HHAlert'
import ReactivateAccountDialog from 'components/customer-details/content/common/reactivate-account-dialog/ReactivateAccountDialog'
import { canUpdateService } from 'data/permissions/permissionsSelectors'
import { getCustomerDetails } from 'middleware/actions/customers'
import T from 'T'
import { formatDateToFEFormat } from 'utils/date'
import { handleErrorWithFetchError } from 'utils/error'

import type { PendingScheduleApi, ReactivationStatusApi } from 'types/api/account/accountReactivation'

type PendingReactivationBannerProps = {
  accountId: string
  accountName: string
  accountNumber?: string
  pendingSchedule?: PendingScheduleApi | null
}

const PendingReactivationBanner = ({ accountId, accountName, accountNumber, pendingSchedule }: PendingReactivationBannerProps) => {
  const dispatch = useDispatch()
  const canEdit = useSelector(canUpdateService, shallowEqual)
  const [isOpenDialog, setIsOpenDialog] = useState(false)
  const [cancelSchedule, { isLoading: isCancelling }] = useCancelScheduledReactivationMutation()
  const lastFailure = pendingSchedule?.lastFailure?.at ? pendingSchedule.lastFailure : undefined

  const refetchAccount = useCallback(() => dispatch(getCustomerDetails({ accountId })), [dispatch, accountId])
  // Immediately, picked over the schedule, restores services now, so the tabs below refetch as the toolbar's reactivate does.
  const handleReactivated = useCallback(
    (status: ReactivationStatusApi) => {
      refetchAccount()
      if (status !== 'SCHEDULED') dispatch(api.util.invalidateTags([CACHE_TAG_CONFIGURED_SERVICES_LIST, CACHE_TAG_WORK_ORDER_LIST]))
    },
    [refetchAccount, dispatch]
  )
  const handleOpenDialog = useCallback(() => setIsOpenDialog(true), [])
  const handleCloseDialog = useCallback(() => setIsOpenDialog(false), [])

  const handleCancel = useCallback(async () => {
    try {
      await cancelSchedule({ accountId }).unwrap()
      toast.success(T.REACTIVATION_SCHEDULE_CANCELLED)
    } catch (error) {
      // Nothing pending any more: it was run or cancelled elsewhere, so the refetch shows the account as it is now.
      if ((error as { status?: number })?.status !== 404) handleErrorWithFetchError(error, T.REACTIVATION_SCHEDULE_CANCEL_FAILED)
    }
    refetchAccount()
  }, [cancelSchedule, accountId, refetchAccount])

  if (pendingSchedule?.type !== 'REACTIVATION') return null

  return (
    <>
      <HHAlert
        action={
          canEdit && (
            <Stack direction="row" gap={1}>
              <Button color="inherit" size="small" onClick={handleOpenDialog}>
                {T.EDIT}
              </Button>
              <Button color="inherit" loading={isCancelling} size="small" onClick={handleCancel}>
                {T.CANCEL}
              </Button>
            </Stack>
          )
        }
        severity="info"
        sx={{ '& .MuiAlert-action': { pt: 0, alignItems: 'center' } }}
      >
        <Typography fontWeight={600} variant="body1">
          {T.SCHEDULED_TO_REACTIVATE_ON.replace('%s', formatDateToFEFormat(pendingSchedule.effectiveDate))}
        </Typography>
        {lastFailure && (
          <Stack alignItems="center" direction="row" gap={0.5}>
            <WarningAmberOutlinedIcon color="warning" fontSize="small" />
            <Typography color="warning.dark" variant="body2">
              {T.SCHEDULED_REACTIVATION_LAST_FAILURE.replace('%s', formatDateToFEFormat(lastFailure.at ?? '')).replace(
                '%s',
                lastFailure.reason ?? ''
              )}
            </Typography>
          </Stack>
        )}
      </HHAlert>
      <ReactivateAccountDialog
        accountId={accountId}
        accountName={accountName}
        accountNumber={accountNumber}
        isOpen={isOpenDialog}
        pendingSchedule={pendingSchedule}
        onClose={handleCloseDialog}
        onReactivated={handleReactivated}
      />
    </>
  )
}

export default PendingReactivationBanner
